import { useState, useEffect, useCallback, createContext, useContext, useRef, Component } from "react";
import ActivityLogs from "./ActivityLogs";
import DeviceManagement from "./DeviceManagement";

// ═══════════════════════════════════════════
// i18n SYSTEM
// ═══════════════════════════════════════════
const translations = {
  en: {
    app: { name: "AbsoluteGuard", tagline: "Your choice. Your freedom." },
    nav: { dashboard: "Dashboard", devices: "Devices", lockout: "Emergency Lock", settings: "Settings", logs: "Activity", lists: "Lists", lock: "Self-Lock" },
    dashboard: {
      protectionStatus: "Protection Status", active: "Active", inactive: "Inactive",
      lockMode: "Self-Lock", streak: "Clean Streak", streakDays: "days clean",
      bestStreak: "Best streak", blockedToday: "Blocked Today", blockedWeek: "Blocked This Week",
      bypassAttempts: "Bypass Attempts", protectionLevel: "Protection Level",
      strict: "Strict", balanced: "Balanced", relaxed: "Relaxed",
      quickActions: "Quick Actions", enableLock: "Activate Self-Lock",
      viewLogs: "View Activity Log",
      weeklyOverview: "7-Day Block History", mo: "Mo", tu: "Tu", we: "We", th: "Th", fr: "Fr", sa: "Sa", su: "Su"
    },
    devices: {
      title: "Devices", subtitle: "Manage all paired devices and pairing tokens",
      pairNew: "Pair New Device", plan: "Plan", usage: "Device Usage",
      slotsRemaining: "slots remaining", upgradePrompt: "Upgrade to add more devices",
      newPairing: "New Device Pairing", connectDevice: "Connect a Device",
      pairingToken: "Pairing Token", setupInstructions: "Setup Instructions",
      step1: "Install AbsoluteGuard on your device", step1sub: "Browser extension, desktop agent, or mobile app",
      step2: "Open the app and tap 'Pair Device'", step2sub: "Scan the QR code or enter the token manually",
      step3: "Enter the pairing token shown here", step3sub: "Token expires in 15 minutes — one use only",
      simulatePair: "Simulate Pairing (Demo)",
      noDevices: "No devices paired yet", noDevicesSub: 'Click "Pair New Device" to get started',
      lastSeen: "Last Seen", registered: "Registered",
      rename: "Rename", disable: "Disable", enable: "Enable", revoke: "Revoke",
      revokeConfirm: "Revoke this device? This cannot be undone.",
      recentEvents: "Recent Device Events",
      privacyNote: "One-time tokens · Hashed storage · 30s heartbeat · Auto-offline after 5 min",
      regenerate: "Regenerate", copy: "Copy", copied: "Copied", scanWithApp: "Scan with AG app",
      limitReached: "Device limit reached. Upgrade your plan to add more.",
      active: "Active", offline: "Offline", disabled: "Disabled", pending: "Pending",
    },
    lock: {
      title: "Self-Lock Mode", description: "Voluntarily lock protection. Once enabled, cannot be disabled until the timer expires.",
      duration: "Lock Duration", days: "Days", partnerUnlock: "Allow trusted partner unlock",
      cooldown: "Enable cooldown (24h delay)", confirm: "I understand this cannot be undone",
      activate: "Activate Self-Lock", active: "Self-Lock Active", timeRemaining: "Time Remaining",
      warning: "This action is irreversible until the timer expires.",
      locked: "Protection is locked", unlockIn: "Unlocks in"
    },
    settings: { title: "Settings", protection: "Protection", language: "Language", theme: "Theme", dark: "Dark", light: "Light" },
    logs: {
      title: "Activity Logs", blocked: "Blocked", bypass: "Bypass Attempt",
      filterAll: "All", filterBlocked: "Blocked", filterBypass: "Bypass"
    },
    lists: {
      title: "Allow / Block Lists", allowList: "Allowed Sites", blockList: "Blocked Sites",
      addDomain: "Add domain...", add: "Add"
    },
    common: { save: "Save", cancel: "Cancel", confirm: "Confirm", justNow: "Just now", minutesAgo: "m ago", hoursAgo: "h ago", daysAgo: "d ago" }
  },
  de: {
    app: { name: "AbsoluteGuard", tagline: "Dein Schutz. Deine Wahl. Deine Freiheit." },
    nav: { dashboard: "Übersicht", devices: "Geräte", lockout: "Notfall-Sperre", settings: "Einstellungen", logs: "Aktivität", lists: "Listen", lock: "Selbstsperre" },
    dashboard: {
      protectionStatus: "Schutzstatus", active: "Aktiv", inactive: "Inaktiv",
      lockMode: "Selbstsperre", streak: "Saubere Serie", streakDays: "Tage clean",
      bestStreak: "Beste Serie", blockedToday: "Heute blockiert", blockedWeek: "Diese Woche blockiert",
      bypassAttempts: "Umgehungsversuche", protectionLevel: "Schutzstufe",
      strict: "Streng", balanced: "Ausgewogen", relaxed: "Entspannt",
      quickActions: "Schnellaktionen", enableLock: "Selbstsperre aktivieren",
      viewLogs: "Aktivitätsverlauf",
      weeklyOverview: "7-Tage-Verlauf", mo: "Mo", tu: "Di", we: "Mi", th: "Do", fr: "Fr", sa: "Sa", su: "So"
    },
    devices: {
      title: "Geräte", subtitle: "Alle gekoppelten Geräte und Pairing-Token verwalten",
      pairNew: "Neues Gerät koppeln", plan: "Plan", usage: "Gerätenutzung",
      slotsRemaining: "Slots verfügbar", upgradePrompt: "Upgrade für mehr Geräte",
      newPairing: "Neues Gerät koppeln", connectDevice: "Gerät verbinden",
      pairingToken: "Pairing-Token", setupInstructions: "Einrichtungsanleitung",
      step1: "AbsoluteGuard auf deinem Gerät installieren", step1sub: "Browser-Erweiterung, Desktop-Agent oder mobile App",
      step2: "App öffnen und 'Gerät koppeln' antippen", step2sub: "QR-Code scannen oder Token manuell eingeben",
      step3: "Den hier angezeigten Pairing-Token eingeben", step3sub: "Token läuft in 15 Minuten ab — einmalig verwendbar",
      simulatePair: "Kopplung simulieren (Demo)",
      noDevices: "Noch keine Geräte gekoppelt", noDevicesSub: 'Klicke auf "Neues Gerät koppeln" um zu starten',
      lastSeen: "Zuletzt gesehen", registered: "Registriert",
      rename: "Umbenennen", disable: "Deaktivieren", enable: "Aktivieren", revoke: "Widerrufen",
      revokeConfirm: "Gerät widerrufen? Dies kann nicht rückgängig gemacht werden.",
      recentEvents: "Letzte Geräteereignisse",
      privacyNote: "Einmalige Token · Gehashte Speicherung · 30s Heartbeat · Auto-Offline nach 5 Min",
      regenerate: "Neu generieren", copy: "Kopieren", copied: "Kopiert", scanWithApp: "Mit AG-App scannen",
      limitReached: "Gerätelimit erreicht. Upgrade für mehr Geräte.",
      active: "Aktiv", offline: "Offline", disabled: "Deaktiviert", pending: "Ausstehend",
    },
    lock: {
      title: "Selbstsperre", description: "Freiwillig den Schutz sperren. Kann nicht vor Ablauf deaktiviert werden.",
      duration: "Sperrdauer", days: "Tage", partnerUnlock: "Vertrauensperson darf entsperren",
      cooldown: "Entsperr-Verzögerung (24Std)", confirm: "Ich verstehe — nicht rückgängig machbar",
      activate: "Selbstsperre aktivieren", active: "Selbstsperre aktiv", timeRemaining: "Verbleibende Zeit",
      warning: "Unwiderruflich bis zum Ablauf des Timers.",
      locked: "Schutz gesperrt", unlockIn: "Entsperrt in"
    },
    settings: { title: "Einstellungen", protection: "Schutz", language: "Sprache", theme: "Design", dark: "Dunkel", light: "Hell" },
    logs: { title: "Aktivitätsverlauf", blocked: "Blockiert", bypass: "Umgehungsversuch", filterAll: "Alle", filterBlocked: "Blockiert", filterBypass: "Umgehung" },
    lists: { title: "Erlaubt / Blockiert", allowList: "Erlaubte Seiten", blockList: "Blockierte Seiten", addDomain: "Domain...", add: "Hinzufügen" },
    common: { save: "Speichern", cancel: "Abbrechen", confirm: "Bestätigen", justNow: "Gerade eben", minutesAgo: "Min. ago", hoursAgo: "Std. ago", daysAgo: "T. ago" }
  },
  bg: {
    app: { name: "AbsoluteGuard", tagline: "Твоят щит. Твоят избор. Твоята свобода." },
    nav: { dashboard: "Табло", devices: "Устройства", lockout: "Аварийно заключване", settings: "Настройки", logs: "Активност", lists: "Списъци", lock: "Заключване" },
    dashboard: {
      protectionStatus: "Статус на защитата", active: "Активна", inactive: "Неактивна",
      lockMode: "Самозаключване", streak: "Чиста серия", streakDays: "дни чист",
      bestStreak: "Най-добра серия", blockedToday: "Блокирани днес", blockedWeek: "Блокирани тази седмица",
      bypassAttempts: "Опити за заобикаляне", protectionLevel: "Ниво на защита",
      strict: "Стриктно", balanced: "Балансирано", relaxed: "Спокойно",
      quickActions: "Бързи действия", enableLock: "Активирай самозаключване",
      viewLogs: "Виж дневника",
      weeklyOverview: "История за 7 дни", mo: "Пн", tu: "Вт", we: "Ср", th: "Чт", fr: "Пт", sa: "Сб", su: "Нд"
    },
    devices: {
      title: "Устройства", subtitle: "Управлявай сдвоените устройства и токени",
      pairNew: "Сдвои ново устройство", plan: "План", usage: "Използване на устройства",
      slotsRemaining: "налични слота", upgradePrompt: "Надгради за повече устройства",
      newPairing: "Ново сдвояване", connectDevice: "Свържи устройство",
      pairingToken: "Токен за сдвояване", setupInstructions: "Инструкции за настройка",
      step1: "Инсталирай AbsoluteGuard на устройството", step1sub: "Разширение за браузър, настолен агент или мобилно приложение",
      step2: "Отвори приложението и натисни 'Сдвои устройство'", step2sub: "Сканирай QR кода или въведи токена ръчно",
      step3: "Въведи показания тук токен", step3sub: "Токенът изтича след 15 минути — еднократна употреба",
      simulatePair: "Симулирай сдвояване (Демо)",
      noDevices: "Все още няма сдвоени устройства", noDevicesSub: 'Натисни "Сдвои ново устройство" за начало',
      lastSeen: "Последно виждан", registered: "Регистриран",
      rename: "Преименувай", disable: "Деактивирай", enable: "Активирай", revoke: "Отмени",
      revokeConfirm: "Да се отмени устройството? Това не може да се върне.",
      recentEvents: "Последни събития на устройства",
      privacyNote: "Еднократни токени · Хеширано съхранение · 30с heartbeat · Авто-офлайн след 5 мин",
      regenerate: "Регенерирай", copy: "Копирай", copied: "Копирано", scanWithApp: "Сканирай с AG приложение",
      limitReached: "Достигнат лимит на устройства. Надгради плана за повече.",
      active: "Активно", offline: "Офлайн", disabled: "Деактивирано", pending: "Изчакващо",
    },
    lock: {
      title: "Самозаключване", description: "Доброволно заключи защитата. Не може да бъде деактивирано преди изтичане на таймера.",
      duration: "Продължителност", days: "Дни", partnerUnlock: "Доверен партньор може да отключи",
      cooldown: "Забавяне на отключване (24ч)", confirm: "Разбирам — необратимо",
      activate: "Активирай", active: "Заключването е активно", timeRemaining: "Оставащо време",
      warning: "Необратимо до изтичане на таймера.",
      locked: "Защитата е заключена", unlockIn: "Отключва се след"
    },
    settings: { title: "Настройки", protection: "Защита", language: "Език", theme: "Тема", dark: "Тъмна", light: "Светла" },
    logs: { title: "Дневник", blocked: "Блокирано", bypass: "Заобикаляне", filterAll: "Всички", filterBlocked: "Блокирани", filterBypass: "Заобикаляне" },
    lists: { title: "Разрешени / Блокирани", allowList: "Разрешени", blockList: "Блокирани", addDomain: "Домейн...", add: "Добави" },
    common: { save: "Запази", cancel: "Отказ", confirm: "Потвърди", justNow: "Току-що", minutesAgo: "мин.", hoursAgo: "ч.", daysAgo: "д." }
  }
};

const langNames = { en: "English", de: "Deutsch", bg: "Български" };
const AppContext = createContext();
const useApp = () => useContext(AppContext);
const T = (lang, path) => path.split('.').reduce((o, k) => o?.[k], translations[lang]) || path;

// ═══════════════════════════════════════════
// ICONS
// ═══════════════════════════════════════════
const I = {
  Shield: (p) => <svg {...p} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>,
  Lock: (p) => <svg {...p} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0110 0v4"/></svg>,
  Brain: (p) => <svg {...p} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2a4 4 0 00-4 4v1a4 4 0 004 4 4 4 0 004-4V6a4 4 0 00-4-4z"/><path d="M8 14a4 4 0 00-4 4 4 4 0 004 4h8a4 4 0 004-4 4 4 0 00-4-4H8z"/></svg>,
  Activity: (p) => <svg {...p} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>,
  Settings: (p) => <svg {...p} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 11-2.83 2.83l-.06-.06a1.65 1.65 0 00-2.82 1.18V21a2 2 0 01-4 0v-.09a1.65 1.65 0 00-1.08-1.51 1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 11-2.83-2.83l.06-.06a1.65 1.65 0 00-.33-1.82 1.65 1.65 0 00-1.18-.82H3a2 2 0 010-4h.09a1.65 1.65 0 001.51-1.08 1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 112.83-2.83l.06.06a1.65 1.65 0 001.82.33H9a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 112.83 2.83l-.06.06a1.65 1.65 0 00-.33 1.82V9c.26.604.852.997 1.51 1.08H21a2 2 0 010 4h-.09c-.658.083-1.25.476-1.51 1.08z"/></svg>,
  List: (p) => <svg {...p} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><circle cx="3" cy="6" r="1" fill="currentColor"/><circle cx="3" cy="12" r="1" fill="currentColor"/><circle cx="3" cy="18" r="1" fill="currentColor"/></svg>,
  Send: (p) => <svg {...p} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>,
  Moon: (p) => <svg {...p} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 12.79A9 9 0 1111.21 3 7 7 0 0021 12.79z"/></svg>,
  Sun: (p) => <svg {...p} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/><line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/><line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/></svg>,
  Flame: (p) => <svg {...p} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M8.5 14.5A2.5 2.5 0 0011 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 11-14 0c0-1.153.433-2.294 1-3a2.5 2.5 0 002.5 2.5z"/></svg>,
  X: (p) => <svg {...p} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>,
  Check: (p) => <svg {...p} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="20 6 9 17 4 12"/></svg>,
  Plus: (p) => <svg {...p} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>,
  Eye: (p) => <svg {...p} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>,
  Globe: (p) => <svg {...p} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z"/></svg>,
  Zap: (p) => <svg {...p} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>,
  Wind: (p) => <svg {...p} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9.59 4.59A2 2 0 1111 8H2m10.59 11.41A2 2 0 1014 16H2m15.73-8.27A2.5 2.5 0 1119.5 12H2"/></svg>,
  Chevron: (p) => <svg {...p} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 18 15 12 9 6"/></svg>,
  AlertTriangle: (p) => <svg {...p} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>,
  Monitor: (p) => <svg {...p} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="3" width="20" height="14" rx="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/></svg>,
};

// ═══════════════════════════════════════════
// MOCK DATA
// ═══════════════════════════════════════════
const mockLogs = [
  { id: 1, type: "blocked", domain: "explicit-site.com", reason: "Adult domain blacklist", time: "2 min ago" },
  { id: 2, type: "blocked", domain: "nsfw-redirect.xyz", reason: "Redirect cloaking detected", time: "18 min ago" },
  { id: 3, type: "bypass", domain: "—", reason: "VPN detection triggered", time: "1 hour ago" },
  { id: 4, type: "blocked", domain: "adult-mirror.net", reason: "Mirror domain detected", time: "2 hours ago" },
  { id: 5, type: "blocked", domain: "popup-ad.com", reason: "Adult ad network", time: "3 hours ago" },
  { id: 6, type: "bypass", domain: "—", reason: "Attempted to disable extension", time: "5 hours ago" },
  { id: 7, type: "blocked", domain: "explicit-video.to", reason: "AI NSFW detection", time: "Yesterday" },
];
// weekData is now live state in AppContext (see AbsoluteGuard root component)

// ═══════════════════════════════════════════
// SHARED COMPONENTS
// ═══════════════════════════════════════════
function Card({ children, style }) {
  return <div style={{ padding: 24, borderRadius: 16, background: "var(--card-bg)", border: "1px solid var(--border)", ...style }}>{children}</div>;
}

function StatCard({ label, value, color, icon, sub, glow }) {
  return (
    <div style={{ padding: 20, borderRadius: 16, background: "var(--card-bg)", border: "1px solid var(--border)", position: "relative", overflow: "hidden" }}>
      {glow && <div style={{ position: "absolute", top: -20, right: -20, width: 80, height: 80, borderRadius: "50%", background: color, opacity: 0.08, filter: "blur(20px)" }} />}
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
        <div style={{ color }}>{icon}</div>
        <span style={{ fontSize: 12, color: "var(--text-muted)", fontWeight: 500 }}>{label}</span>
      </div>
      <div style={{ fontSize: 22, fontWeight: 800, color, fontFamily: "var(--font-d)", letterSpacing: "-0.02em" }}>{value}</div>
      {sub && <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 4 }}>{sub}</div>}
    </div>
  );
}

function LockTimer({ endTime, lang }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const iv = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(iv); }, []);
  const d = Math.max(0, endTime - now);
  const vals = [Math.floor(d / 864e5), Math.floor((d % 864e5) / 36e5), Math.floor((d % 36e5) / 6e4), Math.floor((d % 6e4) / 1e3)];
  const units = lang === "de" ? ["T", "Std", "Min", "Sek"] : lang === "bg" ? ["Д", "Ч", "М", "С"] : ["D", "H", "M", "S"];
  return (
    <div style={{ display: "flex", gap: 12, marginTop: 8 }}>
      {vals.map((v, i) => (
        <div key={i} style={{ textAlign: "center" }}>
          <div style={{ fontSize: 26, fontWeight: 800, fontFamily: "var(--font-d)", color: "var(--text)", fontVariantNumeric: "tabular-nums", background: "var(--hover-bg)", borderRadius: 10, padding: "6px 12px", minWidth: 48 }}>
            {String(v).padStart(2, "0")}
          </div>
          <div style={{ fontSize: 10, color: "var(--text-muted)", marginTop: 4, fontWeight: 600 }}>{units[i]}</div>
        </div>
      ))}
    </div>
  );
}

function Chk({ checked, onChange, label, bold }) {
  return (
    <label style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer", fontSize: 13, color: bold ? "var(--text)" : "var(--text-secondary)", fontWeight: bold ? 600 : 400 }}>
      <div onClick={onChange} style={{ width: 22, height: 22, borderRadius: 6, border: "2px solid", borderColor: checked ? "var(--accent)" : "var(--border)", background: checked ? "var(--accent)" : "transparent", display: "flex", alignItems: "center", justifyContent: "center", transition: "all .15s", flexShrink: 0, cursor: "pointer" }}>
        {checked && <I.Check style={{ width: 14, height: 14, color: "#fff" }} />}
      </div>
      <span>{label}</span>
    </label>
  );
}

function LangSwitcher() {
  const { lang, setLang } = useApp();
  const [open, setOpen] = useState(false);
  return (
    <div style={{ position: "relative" }}>
      <button onClick={() => setOpen(!open)} style={{ display: "flex", alignItems: "center", gap: 6, padding: "6px 12px", borderRadius: 8, border: "1px solid var(--border)", background: "var(--card-bg)", color: "var(--text-secondary)", cursor: "pointer", fontSize: 13, fontWeight: 500 }}>
        <I.Globe style={{ width: 14, height: 14 }} />{langNames[lang]}
      </button>
      {open && <div style={{ position: "absolute", top: "100%", right: 0, marginTop: 4, padding: 4, borderRadius: 10, border: "1px solid var(--border)", background: "var(--card-bg)", boxShadow: "0 8px 32px rgba(0,0,0,0.2)", minWidth: 140, zIndex: 100 }}>
        {Object.entries(langNames).map(([c, n]) => (
          <button key={c} onClick={() => { setLang(c); setOpen(false); }} style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 14px", borderRadius: 6, border: "none", background: lang === c ? "var(--accent-a)" : "transparent", color: lang === c ? "var(--accent)" : "var(--text)", cursor: "pointer", fontSize: 13, width: "100%", fontFamily: "var(--font-b)", fontWeight: lang === c ? 600 : 400 }}>
            {lang === c && <I.Check style={{ width: 14, height: 14 }} />}{n}
          </button>
        ))}
      </div>}
    </div>
  );
}

// ═══════════════════════════════════════════
// PAGES
// ═══════════════════════════════════════════
function DashboardPage({ setPage }) {
  const { lang, protectionActive, streak, lockActive, lockEnd, protectionLevel, setProtectionLevel, bypassCount, blockedToday, blockedWeek, weekData } = useApp();
  const levels = ["strict", "balanced", "relaxed"];
  const lc = { strict: "#ef4444", balanced: "#f59e0b", relaxed: "#22c55e" };
  const days = ["mo", "tu", "we", "th", "fr", "sa", "su"];
  const mx = Math.max(...(weekData || [0]), 1);

  return (
    <div style={{ padding: "32px 36px", maxWidth: 1200 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 28 }}>
        <h1 style={{ fontFamily: "var(--font-d)", fontSize: 28, fontWeight: 800, color: "var(--text)", margin: 0, letterSpacing: "-0.03em" }}>{T(lang, "nav.dashboard")}</h1>
        <LangSwitcher />
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 16, marginBottom: 28 }}>
        <StatCard label={T(lang, "dashboard.protectionStatus")} value={protectionActive ? T(lang, "dashboard.active") : T(lang, "dashboard.inactive")} color={protectionActive ? "#22c55e" : "#ef4444"} icon={<I.Shield style={{ width: 20, height: 20 }} />} glow={protectionActive} />
        <StatCard label={T(lang, "dashboard.streak")} value={`${streak} ${T(lang, "dashboard.streakDays")}`} color="#f59e0b" icon={<I.Flame style={{ width: 20, height: 20 }} />} sub={`${T(lang, "dashboard.bestStreak")}: 42`} />
        <StatCard label={T(lang, "dashboard.blockedToday")} value={String(blockedToday)} color="var(--accent)" icon={<I.Eye style={{ width: 20, height: 20 }} />} />
        <StatCard label={T(lang, "dashboard.blockedWeek")} value={String(blockedWeek)} color="#8b5cf6" icon={<I.Activity style={{ width: 20, height: 20 }} />} />
        <StatCard label={T(lang, "dashboard.bypassAttempts")} value={String(bypassCount)} color="#ef4444" icon={<I.Zap style={{ width: 20, height: 20 }} />} />
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
        <Card>
          <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text-secondary)", marginBottom: 16, textTransform: "uppercase", letterSpacing: ".06em" }}>{T(lang, "dashboard.protectionLevel")}</div>
          <div style={{ display: "flex", gap: 8 }}>
            {levels.map(l => (
              <button key={l} onClick={() => !lockActive && setProtectionLevel(l)} style={{ flex: 1, padding: "10px 0", borderRadius: 10, border: "2px solid", borderColor: protectionLevel === l ? lc[l] : "var(--border)", background: protectionLevel === l ? lc[l] + "18" : "transparent", color: protectionLevel === l ? lc[l] : "var(--text-secondary)", cursor: lockActive ? "not-allowed" : "pointer", fontWeight: 600, fontSize: 13, fontFamily: "var(--font-b)", opacity: lockActive ? .5 : 1 }}>
                {T(lang, `dashboard.${l}`)}
              </button>
            ))}
          </div>
          {lockActive && <div style={{ marginTop: 12, fontSize: 12, color: "#f59e0b", display: "flex", alignItems: "center", gap: 6 }}><I.Lock style={{ width: 14, height: 14 }} />{T(lang, "lock.locked")}</div>}
        </Card>
        <Card>
          <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text-secondary)", marginBottom: 16, textTransform: "uppercase", letterSpacing: ".06em" }}>{T(lang, "dashboard.weeklyOverview")}</div>
          <div style={{ display: "flex", alignItems: "flex-end", gap: 8, height: 100 }}>
            {weekData.map((v, i) => (
              <div key={i} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 6 }}>
                <div style={{ width: "100%", borderRadius: 6, height: Math.max((v / mx) * 80, 4), background: v === 0 ? "var(--border)" : "linear-gradient(180deg, var(--accent), var(--accent-h))", opacity: v === 0 ? .3 : .9, transition: "height .4s ease" }} />
                <span style={{ fontSize: 11, color: "var(--text-muted)" }}>{T(lang, `dashboard.${days[i]}`)}</span>
              </div>
            ))}
          </div>
        </Card>
        <Card>
          <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text-secondary)", marginBottom: 16, textTransform: "uppercase", letterSpacing: ".06em" }}>{T(lang, "dashboard.lockMode")}</div>
          {lockActive ? (
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                <div style={{ width: 10, height: 10, borderRadius: "50%", background: "#22c55e", boxShadow: "0 0 8px #22c55e60", animation: "pulse 2s infinite" }} />
                <span style={{ fontWeight: 700, color: "#22c55e", fontSize: 15 }}>{T(lang, "lock.active")}</span>
              </div>
              <LockTimer endTime={lockEnd} lang={lang} />
            </div>
          ) : (
            <button onClick={() => setPage("lock")} className="ag-dashed-btn" style={{ padding: "10px 20px", borderRadius: 10, border: "2px dashed var(--border)", background: "transparent", color: "var(--text-secondary)", cursor: "pointer", fontFamily: "var(--font-b)", fontSize: 13, width: "100%", transition: "all .15s" }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = "var(--accent)"; e.currentTarget.style.color = "var(--accent)"; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = "var(--border)"; e.currentTarget.style.color = "var(--text-secondary)"; }}>
              <I.Lock style={{ width: 16, height: 16, display: "inline", verticalAlign: "middle", marginRight: 6 }} />{T(lang, "dashboard.enableLock")}
            </button>
          )}
        </Card>
        <Card>
          <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text-secondary)", marginBottom: 16, textTransform: "uppercase", letterSpacing: ".06em" }}>{T(lang, "dashboard.quickActions")}</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {[
              { icon: <I.Activity style={{ width: 18, height: 18 }} />, label: T(lang, "dashboard.viewLogs"), page: "logs" },
            ].map((a, i) => (
              <button key={i} onClick={() => setPage(a.page)} style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 16px", borderRadius: 10, border: a.accent ? "none" : "1px solid var(--border)", background: a.accent ? "linear-gradient(135deg, var(--accent), var(--accent-h))" : "var(--hover-bg)", color: a.accent ? "#fff" : "var(--text)", cursor: "pointer", fontFamily: "var(--font-b)", fontSize: 14, fontWeight: 500, width: "100%", textAlign: "left", transition: "all .15s" }}
                onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-1px)"; }}
                onMouseLeave={e => { e.currentTarget.style.transform = "translateY(0)"; }}>
                {a.icon}{a.label}
              </button>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}

function LockPage() {
  const { lang, lockActive, lockEnd, setLockActive, setLockEnd } = useApp();
  const [dur, setDur] = useState(7);
  const [pUnlock, setPUnlock] = useState(false);
  const [cool, setCool] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [warn, setWarn] = useState(false);

  const activate = () => {
    if (!confirmed) return;
    if (!warn) { setWarn(true); return; }
    setLockActive(true);
    setLockEnd(Date.now() + dur * 864e5);
    setWarn(false);
  };

  if (lockActive) return (
    <div style={{ padding: "32px 36px", maxWidth: 600 }}>
      <h1 style={{ fontFamily: "var(--font-d)", fontSize: 28, fontWeight: 800, color: "var(--text)", margin: "0 0 8px" }}>{T(lang, "lock.title")}</h1>
      <Card style={{ marginTop: 24 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 20 }}>
          <div style={{ width: 12, height: 12, borderRadius: "50%", background: "#22c55e", boxShadow: "0 0 12px #22c55e60", animation: "pulse 2s infinite" }} />
          <span style={{ fontWeight: 700, color: "#22c55e", fontSize: 18, fontFamily: "var(--font-d)" }}>{T(lang, "lock.active")}</span>
        </div>
        <div style={{ fontSize: 13, color: "var(--text-muted)", marginBottom: 16 }}>{T(lang, "lock.timeRemaining")}</div>
        <LockTimer endTime={lockEnd} lang={lang} />
      </Card>
    </div>
  );

  return (
    <div style={{ padding: "32px 36px", maxWidth: 600 }}>
      <h1 style={{ fontFamily: "var(--font-d)", fontSize: 28, fontWeight: 800, color: "var(--text)", margin: "0 0 8px" }}>{T(lang, "lock.title")}</h1>
      <p style={{ color: "var(--text-secondary)", fontSize: 14, lineHeight: 1.6, margin: "0 0 28px" }}>{T(lang, "lock.description")}</p>
      <Card>
        <div style={{ marginBottom: 24 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text-secondary)", marginBottom: 12 }}>{T(lang, "lock.duration")}</div>
          <div style={{ display: "flex", gap: 8 }}>
            {[7, 14, 30, 90].map(d => (
              <button key={d} onClick={() => setDur(d)} style={{ flex: 1, padding: "14px 0", borderRadius: 12, border: "2px solid", borderColor: dur === d ? "var(--accent)" : "var(--border)", background: dur === d ? "var(--accent-a)" : "transparent", color: dur === d ? "var(--accent)" : "var(--text-secondary)", cursor: "pointer", fontWeight: 700, fontSize: 15, fontFamily: "var(--font-d)" }}>
                {d}<div style={{ fontSize: 10, fontWeight: 500, opacity: .7 }}>{T(lang, "lock.days")}</div>
              </button>
            ))}
          </div>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 12, marginBottom: 24 }}>
          <Chk checked={pUnlock} onChange={() => setPUnlock(!pUnlock)} label={T(lang, "lock.partnerUnlock")} />
          <Chk checked={cool} onChange={() => setCool(!cool)} label={T(lang, "lock.cooldown")} />
          <Chk checked={confirmed} onChange={() => { setConfirmed(p => { if (p) setWarn(false); return !p; }); }} label={T(lang, "lock.confirm")} bold />
        </div>
        {warn && <div style={{ padding: "14px 18px", borderRadius: 12, background: "#ef444415", border: "1px solid #ef444440", marginBottom: 16, fontSize: 13, color: "#ef4444", fontWeight: 600, lineHeight: 1.5 }}>⚠ {T(lang, "lock.warning")}</div>}
        <button onClick={activate} disabled={!confirmed} style={{ width: "100%", padding: 14, borderRadius: 12, border: "none", background: confirmed ? (warn ? "linear-gradient(135deg, #ef4444, #dc2626)" : "linear-gradient(135deg, var(--accent), var(--accent-h))") : "var(--border)", color: confirmed ? "#fff" : "var(--text-muted)", cursor: confirmed ? "pointer" : "not-allowed", fontWeight: 700, fontSize: 15, fontFamily: "var(--font-d)", boxShadow: confirmed ? "0 4px 20px var(--accent-g)" : "none", transition: "all .2s" }}>
          <I.Lock style={{ width: 16, height: 16, display: "inline", verticalAlign: "middle", marginRight: 8 }} />{warn ? T(lang, "common.confirm") : T(lang, "lock.activate")}
        </button>
      </Card>
    </div>
  );
}

// LogsPage delegates to the full ActivityLogs component (imported at top of file)
function LogsPage() { return <ActivityLogs />; }

function ListsPage() {
  const { lang } = useApp();
  const [aList, setAList] = useState(["wikipedia.org", "stackoverflow.com", "github.com"]);
  const [bList, setBList] = useState(["explicit-site.com", "nsfw-domain.xyz", "adult-mirror.net"]);
  const [aNew, setANew] = useState("");
  const [bNew, setBNew] = useState("");
  const add = (list, setL, v, setV) => { if (v.trim()) { setL(p => [...p, v.trim()]); setV(""); } };

  const ListEd = ({ title, items, rm, val, setVal, onAdd, color }) => (
    <Card>
      <div style={{ fontSize: 14, fontWeight: 700, color, marginBottom: 16, fontFamily: "var(--font-d)" }}>{title}</div>
      <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
        <input value={val} onChange={e => setVal(e.target.value)} onKeyDown={e => e.key === "Enter" && onAdd()} placeholder={T(lang, "lists.addDomain")} style={{ flex: 1, padding: "8px 14px", borderRadius: 8, border: "1px solid var(--border)", background: "var(--input-bg)", color: "var(--text)", fontSize: 13, fontFamily: "var(--font-b)", outline: "none" }} />
        <button onClick={onAdd} style={{ padding: "8px 16px", borderRadius: 8, border: "none", background: color + "20", color, cursor: "pointer", fontWeight: 600, fontSize: 12 }}>{T(lang, "lists.add")}</button>
      </div>
      {items.map((it, i) => (
        <div key={i} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 12px", borderRadius: 8, background: "var(--hover-bg)", fontSize: 13, marginBottom: 4 }}>
          <span style={{ color: "var(--text)" }}>{it}</span>
          <button onClick={() => rm(i)} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-muted)", padding: 2, display: "flex" }}><I.X style={{ width: 14, height: 14 }} /></button>
        </div>
      ))}
    </Card>
  );

  return (
    <div style={{ padding: "32px 36px", maxWidth: 900 }}>
      <h1 style={{ fontFamily: "var(--font-d)", fontSize: 28, fontWeight: 800, color: "var(--text)", margin: "0 0 24px" }}>{T(lang, "lists.title")}</h1>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
        <ListEd title={T(lang, "lists.allowList")} items={aList} rm={i => setAList(p => p.filter((_, j) => j !== i))} val={aNew} setVal={setANew} onAdd={() => add(aList, setAList, aNew, setANew)} color="#22c55e" />
        <ListEd title={T(lang, "lists.blockList")} items={bList} rm={i => setBList(p => p.filter((_, j) => j !== i))} val={bNew} setVal={setBNew} onAdd={() => add(bList, setBList, bNew, setBNew)} color="#ef4444" />
      </div>
    </div>
  );
}

function SettingsPage() {
  const { lang, setLang, theme, setTheme, protectionLevel, setProtectionLevel } = useApp();
  return (
    <div style={{ padding: "32px 36px", maxWidth: 600 }}>
      <h1 style={{ fontFamily: "var(--font-d)", fontSize: 28, fontWeight: 800, color: "var(--text)", margin: "0 0 28px" }}>{T(lang, "settings.title")}</h1>
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <Card>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12, color: "var(--text)" }}>
            <I.Globe style={{ width: 18, height: 18, color: "var(--accent)" }} /><span style={{ fontSize: 14, fontWeight: 600 }}>{T(lang, "settings.language")}</span>
          </div>
          <div style={{ display: "flex", gap: 6 }}>
            {Object.entries(langNames).map(([c, n]) => (
              <button key={c} onClick={() => setLang(c)} style={{ padding: "6px 14px", borderRadius: 8, border: "1px solid", borderColor: lang === c ? "var(--accent)" : "var(--border)", background: lang === c ? "var(--accent-a)" : "transparent", color: lang === c ? "var(--accent)" : "var(--text-muted)", cursor: "pointer", fontSize: 13, fontWeight: 600, fontFamily: "var(--font-b)" }}>{n}</button>
            ))}
          </div>
        </Card>
        <Card>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12, color: "var(--text)" }}>
            {theme === "dark" ? <I.Moon style={{ width: 18, height: 18, color: "var(--accent)" }} /> : <I.Sun style={{ width: 18, height: 18, color: "var(--accent)" }} />}
            <span style={{ fontSize: 14, fontWeight: 600 }}>{T(lang, "settings.theme")}</span>
          </div>
          <div style={{ display: "flex", gap: 6 }}>
            {["dark", "light"].map(th => (
              <button key={th} onClick={() => setTheme(th)} style={{ padding: "6px 14px", borderRadius: 8, border: "1px solid", borderColor: theme === th ? "var(--accent)" : "var(--border)", background: theme === th ? "var(--accent-a)" : "transparent", color: theme === th ? "var(--accent)" : "var(--text-muted)", cursor: "pointer", fontSize: 13, fontWeight: 600, fontFamily: "var(--font-b)" }}>{T(lang, `settings.${th}`)}</button>
            ))}
          </div>
        </Card>
        <Card>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12, color: "var(--text)" }}>
            <I.Shield style={{ width: 18, height: 18, color: "var(--accent)" }} /><span style={{ fontSize: 14, fontWeight: 600 }}>{T(lang, "settings.protection")}</span>
          </div>
          <div style={{ display: "flex", gap: 6 }}>
            {["strict", "balanced", "relaxed"].map(l => (
              <button key={l} onClick={() => setProtectionLevel(l)} style={{ padding: "6px 14px", borderRadius: 8, border: "1px solid", borderColor: protectionLevel === l ? "var(--accent)" : "var(--border)", background: protectionLevel === l ? "var(--accent-a)" : "transparent", color: protectionLevel === l ? "var(--accent)" : "var(--text-muted)", cursor: "pointer", fontSize: 13, fontWeight: 600, fontFamily: "var(--font-b)" }}>{T(lang, `dashboard.${l}`)}</button>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════
// SIDEBAR
// ═══════════════════════════════════════════
function Sidebar({ page, setPage, collapsed, setCollapsed }) {
  const { lang, theme, setTheme } = useApp();
  const items = [
    { id: "dashboard", icon: I.Shield, label: T(lang, "nav.dashboard") },
    { id: "lock",      icon: I.Lock,     label: T(lang, "nav.lock") },
    { id: "lockout",   icon: I.AlertTriangle, label: T(lang, "nav.lockout") },
    { id: "devices",   icon: I.Monitor,  label: T(lang, "nav.devices") },
    { id: "logs",      icon: I.Activity, label: T(lang, "nav.logs") },
    { id: "lists",     icon: I.List,     label: T(lang, "nav.lists") },
    { id: "settings",  icon: I.Settings, label: T(lang, "nav.settings") },
  ];
  return (
    <div style={{ width: collapsed ? 72 : 260, minHeight: "100vh", background: "var(--sidebar-bg)", borderRight: "1px solid var(--border)", display: "flex", flexDirection: "column", transition: "width .25s cubic-bezier(.4,0,.2,1)", flexShrink: 0, overflow: "hidden" }}>
      <div style={{ padding: collapsed ? "20px 12px" : "20px 24px", display: "flex", alignItems: "center", gap: 12 }}>
        <div style={{ width: 40, height: 40, borderRadius: 12, background: "linear-gradient(135deg, var(--accent), var(--accent-h))", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, boxShadow: "0 4px 16px var(--accent-g)" }}>
          <I.Shield style={{ width: 22, height: 22, color: "#fff" }} />
        </div>
        {!collapsed && <div style={{ overflow: "hidden" }}>
          <div style={{ fontFamily: "var(--font-d)", fontWeight: 800, fontSize: 17, color: "var(--text)", letterSpacing: "-0.02em" }}>{T(lang, "app.name")}</div>
          <div style={{ fontSize: 11, color: "var(--text-muted)", whiteSpace: "nowrap" }}>{T(lang, "app.tagline")}</div>
        </div>}
      </div>
      <nav style={{ padding: "8px 12px", flex: 1, display: "flex", flexDirection: "column", gap: 2 }}>
        {items.map(it => {
          const a = page === it.id;
          return (
            <button key={it.id} onClick={() => setPage(it.id)} style={{ display: "flex", alignItems: "center", gap: 12, padding: collapsed ? "12px 14px" : "10px 14px", borderRadius: 10, border: "none", cursor: "pointer", background: a ? "var(--nav-active)" : "transparent", color: a ? "var(--accent)" : "var(--text-secondary)", fontFamily: "var(--font-b)", fontSize: 14, fontWeight: a ? 600 : 400, justifyContent: collapsed ? "center" : "flex-start", width: "100%", textAlign: "left", transition: "all .15s" }}
              onMouseEnter={e => !a && (e.currentTarget.style.background = "var(--hover-bg)")}
              onMouseLeave={e => !a && (e.currentTarget.style.background = "transparent")}>
              <it.icon style={{ width: 20, height: 20, flexShrink: 0 }} />{!collapsed && <span>{it.label}</span>}
            </button>
          );
        })}
      </nav>
      <div style={{ padding: 12, borderTop: "1px solid var(--border)" }}>
        <button onClick={() => setTheme(theme === "dark" ? "light" : "dark")} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 14px", borderRadius: 10, border: "none", cursor: "pointer", width: "100%", background: "transparent", color: "var(--text-secondary)", fontSize: 13, fontFamily: "var(--font-b)", justifyContent: collapsed ? "center" : "flex-start" }}>
          {theme === "dark" ? <I.Sun style={{ width: 18, height: 18 }} /> : <I.Moon style={{ width: 18, height: 18 }} />}
          {!collapsed && <span>{theme === "dark" ? T(lang, "settings.light") : T(lang, "settings.dark")}</span>}
        </button>
        <button onClick={() => setCollapsed(!collapsed)} style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: 8, borderRadius: 10, border: "none", cursor: "pointer", width: "100%", background: "transparent", color: "var(--text-muted)", marginTop: 4 }}>
          <I.Chevron style={{ width: 16, height: 16, transform: collapsed ? "rotate(0)" : "rotate(180deg)", transition: "transform .2s" }} />
        </button>
      </div>
    </div>
  );
}


// ═══════════════════════════════════════════
// GLOBAL STYLES — injected once, not on every render
// ═══════════════════════════════════════════
function useGlobalStyles(vars) {
  useEffect(() => {
    const existing = document.getElementById("ag-global-styles");
    if (existing) existing.remove();
    const el = document.createElement("style");
    el.id = "ag-global-styles";
    el.textContent = `
      @keyframes pulse{0%,100%{opacity:1}50%{opacity:.5}}
      *{box-sizing:border-box;margin:0;padding:0}
      ::-webkit-scrollbar{width:6px}
      ::-webkit-scrollbar-track{background:transparent}
      ::-webkit-scrollbar-thumb{background:var(--border);border-radius:3px}
      ::placeholder{color:var(--text-muted)}
      input:focus{border-color:var(--accent)!important}
    `;
    document.head.appendChild(el);
  }, []); // only on mount — vars handled via CSS custom properties on root div
}


// ═══════════════════════════════════════════
// ERROR BOUNDARY — catches render crashes per page
// ═══════════════════════════════════════════
class PageErrorBoundary extends Component {
  constructor(props) { super(props); this.state = { error: null }; }
  static getDerivedStateFromError(e) { return { error: e }; }
  render() {
    if (this.state.error) {
      return (
        <div style={{ padding: "40px 36px" }}>
          <div style={{ padding: 24, borderRadius: 12, background: "#ef444410", border: "1px solid #ef444430", color: "#ef4444", fontSize: 13 }}>
            <div style={{ fontWeight: 700, marginBottom: 8 }}>⚠ Page failed to render</div>
            <div style={{ fontFamily: "monospace", fontSize: 11, opacity: 0.7 }}>{this.state.error.message}</div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

// ═══════════════════════════════════════════
// APP ROOT
// ═══════════════════════════════════════════
export default function AbsoluteGuard() {
  const [lang, setLang] = useState("en");
  const [theme, setTheme] = useState("dark");
  const [page, setPage] = useState("dashboard");
  const [collapsed, setCollapsed] = useState(false);
  const [protectionActive, setProtectionActive] = useState(true);
  const [streak, setStreak] = useState(0);
  const [protectionLevel, setProtectionLevel] = useState("strict");
  const [lockActive, setLockActive] = useState(false);
  const [lockEnd, setLockEnd] = useState(0);
  const [bypassCount, setBypassCount] = useState(0);
  const [blockedToday, setBlockedToday] = useState(0);
  const [blockedWeek, setBlockedWeek] = useState(0);
  const [weekData, setWeekData] = useState([0,0,0,0,0,0,0]);
  const [apiReady, setApiReady] = useState(false);

// Load real data from the Worker API on mount
useEffect(() => {
  loadDashboardData();
}, []);

async function loadDashboardData() {
  try {
    const { supabase, USER_ID } = await import('./supabase.js');
    
    const now = new Date();
    const todayStart = new Date(now.setHours(0,0,0,0)).toISOString();
    const weekStart = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString();

    // Load protection config
    const { data: config } = await supabase
      .from('protection_config')
      .select('*')
      .eq('user_id', USER_ID)
      .maybeSingle();

    // Load bypass attempts today
    const { data: attemptsToday } = await supabase
      .from('unlock_attempts')
      .select('*')
      .eq('user_id', USER_ID)
      .gte('attempted_at', todayStart);

    // Load bypass attempts this week
    const { data: attemptsWeek } = await supabase
      .from('unlock_attempts')
      .select('*')
      .eq('user_id', USER_ID)
      .gte('attempted_at', weekStart);

    if (config) {
      // Calculate lock status
      const lockUntil = config.lock_until ? new Date(config.lock_until) : null;
      const isLocked = lockUntil && lockUntil > new Date();

      setProtectionActive(true);
      setLockActive(isLocked || false);
      setLockEnd(lockUntil ? lockUntil.getTime() : 0);
      setBypassCount(config.attempt_count || 0);

      // Calculate streak
      const streakStart = config.streak_start ? new Date(config.streak_start) : null;
      if (streakStart) {
        const days = Math.floor((new Date() - streakStart) / (1000 * 60 * 60 * 24));
        setStreak(days);
      }
    }

    // Blocked today = bypass attempts today
    setBlockedToday(attemptsToday?.length || 0);
    setBlockedWeek(attemptsWeek?.length || 0);

    // Build week data for chart
    if (attemptsWeek) {
      const days = [0,0,0,0,0,0,0];
      attemptsWeek.forEach(attempt => {
        const day = new Date(attempt.attempted_at).getDay();
        days[day === 0 ? 6 : day - 1]++;
      });
      setWeekData(days);
    }

    setApiReady(true);
  } catch (err) {
    console.error('Failed to load dashboard data:', err);
    setApiReady(true);
  }
}

const ctx = { lang, setLang, theme, setTheme, protectionActive, streak, protectionLevel, setProtectionLevel, lockActive, setLockActive, lockEnd, setLockEnd, bypassCount, blockedToday, blockedWeek, weekData, apiReady };

  const dk = {
    "--bg": "#0a0a0f", "--sidebar-bg": "#0f0f18", "--card-bg": "#141420", "--border": "#1e1e30",
    "--hover-bg": "#1a1a2e", "--nav-active": "#1a1a30", "--input-bg": "#12121e",
    "--text": "#eeeef0", "--text-secondary": "#9898b0", "--text-muted": "#5c5c78",
    "--accent": "#6366f1", "--accent-h": "#4f46e5", "--accent-g": "#6366f130", "--accent-a": "#6366f118",
    "--font-d": "'DM Sans', system-ui, sans-serif", "--font-b": "'DM Sans', system-ui, sans-serif",
  };
  const lt = {
    "--bg": "#f7f7fa", "--sidebar-bg": "#ffffff", "--card-bg": "#ffffff", "--border": "#e4e4ec",
    "--hover-bg": "#f0f0f6", "--nav-active": "#eef0ff", "--input-bg": "#f4f4f8",
    "--text": "#1a1a2e", "--text-secondary": "#5c5c78", "--text-muted": "#9898b0",
    "--accent": "#6366f1", "--accent-h": "#4f46e5", "--accent-g": "#6366f130", "--accent-a": "#6366f118",
    "--font-d": "'DM Sans', system-ui, sans-serif", "--font-b": "'DM Sans', system-ui, sans-serif",
  };

  useGlobalStyles();
  const vars = theme === "dark" ? dk : lt;
  const renderPage = () => {
    switch(page) {
      case "dashboard": return <DashboardPage setPage={setPage} />;
      case "lock":      return <LockPage />;
      case "devices":   return <DeviceManagement lang={lang} />;
      case "logs":      return <LogsPage />;
      case "lists":     return <ListsPage />;
      case "settings":  return <SettingsPage />;
      default:          return <DashboardPage setPage={setPage} />;
    }
  };

  return (
    <AppContext.Provider value={ctx}>
      <div style={{ ...vars, display: "flex", minHeight: "100vh", background: "var(--bg)", color: "var(--text)", fontFamily: "var(--font-b)" }}>
        {/* NOTE: Move this to public/index.html <head> */}
{/* Global styles injected once via useGlobalStyles() hook below */}
        <Sidebar page={page} setPage={setPage} collapsed={collapsed} setCollapsed={setCollapsed} />
        <main style={{ flex: 1, overflow: "auto", minHeight: "100vh" }}>
          <PageErrorBoundary key={page}>{renderPage()}</PageErrorBoundary>
        </main>
      </div>
    </AppContext.Provider>
  );
}